import React, { useEffect, useState } from 'react';
import { absorb, askChoices } from './calculationPrompt';
import { basisOf, exampleAmounts, money, percentEntry, percentStored } from './accountantModel';
import AppSelect, { useAppSelectDismiss } from './AppSelect';
import CommitmentFilter from './CommitmentFilter';

const BASIS_CHOICES = [
  { key: 'cost_this_period', label: 'Project costs' },
  { key: 'gross_owner_billing_period', label: 'Owner billing' },
  { key: 'revenue_this_period', label: 'Revenue this period' },
  { key: 'current_contract_value', label: 'Current contract value' },
  { key: 'total_commitment_amount', label: 'Commitment amount' },
  { key: 'manual_amount', label: 'Each period' },
];

const PROJECT_COST = [
  { value: 'always', label: 'Creates a project cost' },
  { value: 'never', label: 'Journal entry only' },
  { value: 'project_choice', label: 'Each project decides' },
];

function destinationOptions(costCodes, source) {
  const rows = costCodes || [];
  if (!source?.cost_type_vuid) return rows;
  const matched = rows.filter((row) => row.cost_type_vuid === source.cost_type_vuid);
  return matched.length ? matched : rows;
}

function basisLabel(basis) {
  return BASIS_CHOICES.find((item) => item.key === basis)?.label || 'Project costs';
}

function accountName(accounts, line) {
  if (!line) return '';
  if (line.strategy === 'cost_type_expense') return 'Cost type expense account';
  if (line.strategy === 'insurance_recovery') return 'Insurance recovery';
  if (line.strategy === 'accrued_insurance') return 'Accrued insurance';
  const account = (accounts || []).find((item) => item.vuid === line.account_vuid);
  return account?.account_name || line.role_label || 'Choose an account';
}

function Pencil({ label, onClick }) {
  return (
    <button type="button" className="pc-calc-pencil" aria-label={label} onClick={onClick}>
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
      </svg>
    </button>
  );
}

const FIELD_PLACEHOLDER = {
  rate: '2% or $6 per $1,000',
  debit: 'GL account name',
  credit: 'GL account name',
  destination: 'Cost code',
};

function Ask({ name, accounts, costTypes, costCodes, onApply, finishLabel = 'Done' }) {
  const [input, setInput] = useState('');
  const [slots, setSlots] = useState(null);
  const [pending, setPending] = useState('');
  const [heading, setHeading] = useState('What should this calculation do?');
  const [turns, setTurns] = useState([]);
  const [remaining, setRemaining] = useState(0);
  const [selected, setSelected] = useState('');
  const choices = pending ? askChoices(pending, { accounts, costTypes, costCodes }) : { freeform: true, options: [] };
  const finalStep = Boolean(pending) && remaining <= 1;
  const showText = !pending || choices.freeform;
  const showAction = showText || finalStep;

  const send = (event, answer, shown) => {
    event?.preventDefault();
    const text = String(answer == null ? input : answer).trim();
    if (!text) return;
    const result = absorb(slots, text, { accounts, costTypes, costCodes, name, pending });
    setInput('');
    setSelected('');
    setSlots(result.slots);
    setTurns((rows) => [...rows, shown || text]);
    if (result.question) {
      setPending(result.question.key);
      setRemaining(result.questions.length);
      setHeading(result.reply || result.question.text);
      return;
    }
    setPending('');
    setRemaining(0);
    onApply(result.draft, result.slots);
  };

  return (
    <form className="pc-calc-agent" onSubmit={send}>
      <div className="pc-calc-agent-copy">
        <h2>{heading}</h2>
        {!turns.length ? <p>Describe it in a sentence. I’ll ask if something is still missing.</p> : null}
      </div>
      {turns.length ? (
        <div className="pc-calc-agent-thread">
          {turns.map((text, index) => <p key={`${index}-${text}`} className="is-user">{text}</p>)}
        </div>
      ) : null}
      <div className="pc-calc-composer">
        {choices.options.length ? (
          <div className="pc-calc-agent-choices">
            {choices.options.map((option) => (
              <button
                key={option.answer}
                type="button"
                className={!option.skip && selected === option.answer ? 'is-on' : ''}
                onClick={() => {
                  if (option.skip || choices.freeform || !finalStep) send(null, option.answer, option.label);
                  else setSelected(option.answer);
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        ) : null}
        {showText ? (
          <textarea
            rows={3}
            value={input}
            aria-label="Describe this calculation"
            placeholder={FIELD_PLACEHOLDER[pending] || 'Add 2% to all costs coded to a subcontract cost type, create a project cost against the cost type expense account, and credit the insurance GL account.'}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                send();
              }
            }}
          />
        ) : null}
        {showAction ? (
          <div className="pc-calc-composer-actions">
            <button type="button" className="btn-claret" onClick={() => {
              const chosen = choices.options.find((option) => option.answer === selected);
              send(null, showText ? undefined : selected, showText ? undefined : chosen?.label);
            }}>
              {finalStep ? finishLabel : 'Next'}
            </button>
          </div>
        ) : null}
      </div>
    </form>
  );
}

function Built({
  editing,
  name,
  setName,
  description,
  setDescription,
  parameters,
  setParameters,
  namedValues,
  setNamedValues,
  accountingLines,
  setAccountingLines,
  accounts,
  costTypes,
  source,
  setSource,
  costCodes,
  destination,
  onDestination,
  onRestart,
  calculationType,
  setCalculationType,
  commitments,
  excludedCommitments,
  setExcludedCommitments,
}) {
  const [edit, setEdit] = useState('');
  const [draft, setDraft] = useState('');
  const [menu, setMenu] = useState('');
  useAppSelectDismiss(setMenu);
  const basis = basisOf(namedValues);
  const rate = (parameters || []).find((row) => row.key === 'rate') || (parameters || []).find((row) => row.display === 'percent' || row.display === 'per_thousand');
  const debit = (accountingLines || []).find((line) => line.side !== 'credit');
  const credits = (accountingLines || []).filter((line) => line.side === 'credit');
  const example = exampleAmounts(parameters, namedValues);
  const sample = Object.values(example).find((row) => row.complete);
  const rated = (parameters || []).filter((row) => row.key && (row.display === 'per_thousand' || row.display === 'percent') && row.defaultValue);
  const rateText = (row) => (
    row.display === 'per_thousand' ? `$${row.defaultValue} per $1,000` : `${percentEntry(row.defaultValue)}%`
  );
  const rateLabel = rated.map((row) => {
    const text = rateText(row);
    if (rated.length < 2 || !row.label) return text;
    const role = row.label.replace(/\s*rate$/i, '').toLowerCase();
    return role ? `a ${text} ${role}` : text;
  }).join(' and ');
  const policy = PROJECT_COST.find((item) => item.value === (debit?.job_cost_policy || 'never'))?.label || 'Journal entry only';
  const amountLabel = (line) => (namedValues || []).find((item) => item.key === line?.amount_key)?.label || '';
  const withAmount = (phrase, line) => {
    const label = amountLabel(line);
    return credits.length > 1 && label ? `${phrase} for the ${label.toLowerCase()}` : phrase;
  };
  const debitName = debit?.strategy === 'cost_type_expense' ? 'Cost type expense account' : accountName(accounts, debit);
  const debitPhrase = withAmount(debit?.strategy === 'cost_type_expense' ? 'the cost type expense account' : debitName, debit);
  const creditShown = (line) => withAmount(accountName(accounts, line), line);
  const creditPhrase = credits.map(creditShown).filter(Boolean).join(' and ');
  const basisPhrase = basis === 'gross_owner_billing_period'
    ? 'owner billing'
    : basis === 'revenue_this_period'
      ? 'revenue from this period'
      : basis === 'current_contract_value'
        ? 'current contract value'
        : basis === 'total_commitment_amount'
          ? 'the total commitment amount, including change orders'
          : basis === 'manual_amount'
        ? 'the amount entered each period'
        : 'project costs';
  const sourcePhrase = basis === 'cost_this_period' && source?.cost_type_label ? ` coded to ${source.cost_type_label}` : '';
  const timingPhrase = calculationType === 'cumulative'
    ? 'A new amount is recorded only when the amount it is based on changes.'
    : 'It calculates every period.';
  const blurb = basis === 'manual_amount' || !rateLabel
    ? `${policy}, debits ${debitPhrase}, and credits ${creditPhrase}. ${timingPhrase}`
    : `${rateLabel} of ${basisPhrase}${sourcePhrase}. ${policy}, debits ${debitPhrase}, and credits ${creditPhrase}. ${timingPhrase}`;
  useEffect(() => {
    if (blurb && blurb !== description) setDescription(blurb);
  }, [blurb, description, setDescription]);

  const open = (key, value) => {
    setEdit((current) => (current === key ? '' : key));
    setDraft(value || '');
    setMenu('');
  };

  const setBasis = (key) => {
    setNamedValues((rows) => rows.map((row) => ({
      ...row,
      terms: (row.terms || []).map((term) => (term.kind === 'input' ? { ...term, key } : term)),
    })));
    setEdit('');
  };

  const saveRate = (row) => {
    if (!row) return;
    setParameters((rows) => rows.map((item) => (
      item.id === row.id ? { ...item, defaultValue: item.display === 'percent' ? percentStored(draft) : draft } : item
    )));
    setEdit('');
  };

  const setLine = (id, patch) => {
    setAccountingLines((rows) => rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  return (
    <div className="pc-calc-agent pc-calc-agent-built">
      <div className="pc-calc-agent-name">
        {edit === 'name' ? (
          <input aria-label="Name" value={draft} onChange={(event) => setDraft(event.target.value)} onBlur={() => { setName(draft.trim() || name); setEdit(''); }} />
        ) : <h2>{name}</h2>}
        {editing && edit !== 'name' ? <Pencil label="Edit name" onClick={() => open('name', name)} /> : null}
      </div>
      {blurb ? <p className="pc-calc-agent-summary">{blurb}</p> : null}
      <div className="pc-calc-agent-rows">
        {(rated.length > 1 ? rated : (rate ? [rate] : [])).map((row) => (
          <div className="pc-calc-agent-row" key={row.id}>
            <div>{rated.length > 1 ? (row.label || 'Rate') : 'Rate'}</div>
            {edit === `rate:${row.id}` ? (
              <span className="pc-calc-agent-inline">
                <input aria-label={row.label || 'Rate'} value={draft} onChange={(event) => setDraft(event.target.value)} />
                <span>{row.display === 'per_thousand' ? 'per $1,000' : '%'}</span>
                <button type="button" className="pc-calc-agent-done" onClick={() => saveRate(row)}>Done</button>
              </span>
            ) : <div>{rateText(row)}</div>}
            {editing && edit !== `rate:${row.id}` ? <Pencil label={`Edit ${row.label || 'rate'}`} onClick={() => open(`rate:${row.id}`, row.display === 'percent' ? percentEntry(row.defaultValue) : String(row.defaultValue || ''))} /> : <span />}
          </div>
        ))}
        <div className="pc-calc-agent-row">
          <div>Based on</div>
          {edit === 'basis' ? (
            <span className="pc-calc-agent-choices">
              {BASIS_CHOICES.map((item) => (
                <button key={item.key} type="button" className={basis === item.key ? 'is-on' : ''} onClick={() => setBasis(item.key)}>{item.label}</button>
              ))}
            </span>
          ) : <div>{basisLabel(basis)}</div>}
          {editing && edit !== 'basis' ? <Pencil label="Edit what this is based on" onClick={() => open('basis')} /> : <span />}
        </div>
        {basis === 'total_commitment_amount' && commitments ? (
          <CommitmentFilter
            id="draft-commitment-filter"
            open={menu}
            setOpen={setMenu}
            commitments={commitments}
            excluded={excludedCommitments || []}
            onChange={setExcludedCommitments || (() => {})}
            disabled={!editing}
          />
        ) : null}
        <div className="pc-calc-agent-row">
          <div>Records</div>
          {edit === 'timing' ? (
            <span className="pc-calc-agent-choices">
              <button type="button" className={calculationType === 'cumulative' ? '' : 'is-on'} onClick={() => { if (setCalculationType) setCalculationType('periodic'); setEdit(''); }}>Every period</button>
              <button type="button" className={calculationType === 'cumulative' ? 'is-on' : ''} onClick={() => { if (setCalculationType) setCalculationType('cumulative'); setEdit(''); }}>When the basis changes</button>
            </span>
          ) : <div>{calculationType === 'cumulative' ? 'Only when the amount it is based on changes' : 'Every period'}</div>}
          {editing && setCalculationType && edit !== 'timing' ? <Pencil label="Edit when this records an amount" onClick={() => open('timing')} /> : <span />}
        </div>
        {basis === 'cost_this_period' ? (
          <div className="pc-calc-agent-row">
            <div>Costs from</div>
            {edit === 'source' ? (
              <AppSelect
                id="agent-source"
                open={menu === 'agent-source'}
                setOpen={setMenu}
                menuClassName="w-full"
                value={source?.cost_type_label || ''}
                placeholder="All cost types"
                searchPlaceholder="Search cost types"
                options={[
                  { vuid: '', primary: 'All cost types' },
                  ...(costTypes || []).map((type) => ({
                    vuid: type.vuid,
                    primary: type.cost_type || type.abbreviation || '',
                  })),
                ]}
                onSelect={(vuid) => {
                  const type = (costTypes || []).find((row) => row.vuid === vuid);
                  if (setSource) {
                    setSource(type
                      ? { cost_type_vuid: type.vuid, cost_type_label: type.cost_type || type.abbreviation || '' }
                      : { cost_type_vuid: '', cost_type_label: '' });
                  }
                  setEdit('');
                }}
              />
            ) : <div>{source?.cost_type_label || 'All cost types'}</div>}
            {editing && edit !== 'source' ? <Pencil label="Edit cost type" onClick={() => open('source')} /> : <span />}
          </div>
        ) : null}
        <div className="pc-calc-agent-row">
          <div>Debit</div>
          {edit === 'debit' ? (
            <AppSelect
              id="agent-debit"
              open={menu === 'agent-debit'}
              setOpen={setMenu}
              menuClassName="w-full"
              value={accountName(accounts, debit)}
              placeholder="Choose an account"
              searchPlaceholder="Search accounts"
              options={[
                { vuid: 'cost_type_expense', primary: 'Cost type expense account' },
                ...(accounts || []).map((account) => ({
                  vuid: account.vuid,
                  primary: account.account_name,
                  secondary: account.account_number,
                })),
              ]}
              onSelect={(vuid) => {
                if (!debit) return;
                if (vuid === 'cost_type_expense') {
                  setLine(debit.id, { strategy: 'cost_type_expense', account_vuid: '', role_label: 'Project destination expense account' });
                } else {
                  const account = (accounts || []).find((item) => item.vuid === vuid);
                  setLine(debit.id, { strategy: 'configured_account', account_vuid: vuid, role_label: account?.account_name || '' });
                }
                setEdit('');
              }}
            />
          ) : <div>{withAmount(debitName, debit)}</div>}
          {editing && edit !== 'debit' ? <Pencil label="Edit debit account" onClick={() => open('debit')} /> : <span />}
        </div>
        {(credits.length ? credits : [null]).map((line) => (
          <div className="pc-calc-agent-row" key={line?.id || 'credit'}>
            <div>Credit</div>
            {edit === `credit:${line?.id || 'credit'}` ? (
              <AppSelect
                id={`agent-credit-${line?.id || 'credit'}`}
                open={menu === `agent-credit-${line?.id || 'credit'}`}
                setOpen={setMenu}
                menuClassName="w-full"
                value={accountName(accounts, line)}
                placeholder="Choose an account"
                searchPlaceholder="Search accounts"
                options={(accounts || []).map((account) => ({
                  vuid: account.vuid,
                  primary: account.account_name,
                  secondary: account.account_number,
                }))}
                onSelect={(vuid) => {
                  if (!line) return;
                  const account = (accounts || []).find((item) => item.vuid === vuid);
                  setLine(line.id, { strategy: 'configured_account', account_vuid: vuid, role_label: account?.account_name || '' });
                  setEdit('');
                }}
              />
            ) : <div>{line ? creditShown(line) : 'Choose an account'}</div>}
            {editing && edit !== `credit:${line?.id || 'credit'}` ? <Pencil label="Edit credit account" onClick={() => open(`credit:${line?.id || 'credit'}`)} /> : <span />}
          </div>
        ))}
        <div className="pc-calc-agent-row">
          <div>Project cost</div>
          {edit === 'policy' ? (
            <span className="pc-calc-agent-choices">
              {PROJECT_COST.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  className={(debit?.job_cost_policy || 'never') === item.value ? 'is-on' : ''}
                  onClick={() => {
                    if (debit) setLine(debit.id, { job_cost_policy: item.value });
                    setEdit('');
                  }}
                >
                  {item.label}
                </button>
              ))}
            </span>
          ) : <div>{policy}</div>}
          {editing && edit !== 'policy' ? <Pencil label="Edit project cost" onClick={() => open('policy')} /> : <span />}
        </div>
        {(debit?.job_cost_policy || 'never') === 'always' ? (
          <div className="pc-calc-agent-row">
            <div>Cost code</div>
            {edit === 'destination' ? (
              <AppSelect
                id="agent-destination"
                open={menu === 'agent-destination'}
                setOpen={setMenu}
                menuClassName="w-full"
                value={destination?.destination_cost_code_label || ''}
                placeholder="Choose a cost code"
                searchPlaceholder="Search cost codes"
                options={destinationOptions(costCodes, source).map((row) => ({
                  vuid: row.key,
                  primary: `${row.code || ''} — ${row.description || ''}`.replace(/ — $/, ''),
                  secondary: row.abbreviation || row.cost_type || '',
                }))}
                onSelect={(key) => {
                  const row = destinationOptions(costCodes, source).find((item) => item.key === key);
                  if (row && onDestination) onDestination(row);
                  setEdit('');
                }}
              />
            ) : <div>{destination?.destination_cost_code_label || 'Not chosen'}</div>}
            {editing && edit !== 'destination' ? <Pencil label="Edit cost code" onClick={() => open('destination')} /> : <span />}
          </div>
        ) : null}
      </div>
      {credits.length > 1 ? (namedValues || []).filter((row) => example[row.id]?.complete).map((row) => (
        <p key={row.id} className="pc-calc-agent-example">
          {row.label}: {example[row.id].steps.map((step, index) => `${index ? ` ${(row.operators || [])[index - 1] === 'add' ? '+' : (row.operators || [])[index - 1] === 'sub' ? '−' : (row.operators || [])[index - 1] === 'div' ? '÷' : '×'} ` : ''}${step}`).join('')} = {money(example[row.id].value)}
        </p>
      )) : sample ? (
        <p className="pc-calc-agent-example">
          Example {sample.steps.map((step, index) => `${index ? ` ${(namedValues[0]?.operators || [])[index - 1] === 'add' ? '+' : (namedValues[0]?.operators || [])[index - 1] === 'sub' ? '−' : (namedValues[0]?.operators || [])[index - 1] === 'div' ? '÷' : '×'} ` : ''}${step}`).join('')} = {money(sample.value)}
        </p>
      ) : null}
      {editing ? <button type="button" className="pc-calc-agent-again" onClick={onRestart}>Describe it again</button> : null}
    </div>
  );
}

export default function CalculationAgent({
  asking,
  onBuilt,
  onRestart,
  editing,
  name,
  setName,
  description,
  setDescription,
  parameters,
  setParameters,
  namedValues,
  setNamedValues,
  accountingLines,
  setAccountingLines,
  accounts,
  costTypes,
  source,
  setSource,
  costCodes,
  destination,
  onDestination,
  finishLabel,
  calculationType,
  setCalculationType,
  commitments,
  excludedCommitments,
  setExcludedCommitments,
}) {
  if (asking) {
    return (
      <Ask
        name={name}
        accounts={accounts}
        costTypes={costTypes}
        costCodes={costCodes}
        finishLabel={finishLabel}
        onApply={(draft, slots) => {
          setDescription(draft.description || '');
          setParameters(draft.parameters || []);
          setNamedValues(draft.namedValues || []);
          setAccountingLines(draft.accountingLines || []);
          if (setSource) setSource(draft.source || { cost_type_vuid: '', cost_type_label: '' });
          if (setCalculationType) setCalculationType(draft.calculationType === 'cumulative' ? 'cumulative' : 'periodic');
          if (onDestination) onDestination(slots?.destination || null);
          onBuilt(draft, slots);
        }}
      />
    );
  }
  return (
    <Built
      editing={editing}
      name={name}
      setName={setName}
      description={description}
      setDescription={setDescription}
      parameters={parameters}
      setParameters={setParameters}
      namedValues={namedValues}
      setNamedValues={setNamedValues}
      accountingLines={accountingLines}
      setAccountingLines={setAccountingLines}
      accounts={accounts}
      costTypes={costTypes}
      source={source}
      setSource={setSource}
      costCodes={costCodes}
      destination={destination}
      onDestination={onDestination}
      onRestart={onRestart}
      calculationType={calculationType}
      setCalculationType={setCalculationType}
      commitments={commitments}
      excludedCommitments={excludedCommitments}
      setExcludedCommitments={setExcludedCommitments}
    />
  );
}
